import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#050810]" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Link to="/home">
            <Button variant="ghost" className="text-[#8A92A0] hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 px-6">
        <article className="max-w-3xl mx-auto">
          <header className="mb-12">
            <h1 className="font-['Unbounded'] text-4xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-[#8A92A0]">Last updated: February 2026</p>
          </header>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                StardomFans Ltd. ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our Platform. 
                Please read this policy carefully to understand our practices regarding your personal data.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                We collect information you provide directly to us:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>Account information (name, email — you set your own password; StardomFans Ltd. does <strong>not</strong> use Google or other third-party sign-in)</li>
                <li>Artist profile information (stage name, bio, genre, location)</li>
                <li>Content you upload (music, videos, images)</li>
                <li>Event and product information</li>
                <li>Payment information (processed securely by our PCI-DSS Level 1 processor)</li>
                <li>Identity verification documents (a government-issued document plus a live selfie), which are held in an encrypted store dedicated to verification and are never publicly retrievable</li>
                <li>Communications with us (routed through the in-app Admin In-Mail — we do <strong>not</strong> send outbound marketing emails)</li>
              </ul>
              <p className="text-[#8A92A0] leading-relaxed mt-4">
                We automatically collect only the minimum required to operate the Platform:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2 mt-2">
                <li>Device and browser information (server logs only)</li>
                <li>IP address (kept only for security &amp; abuse prevention)</li>
                <li>First-party session cookies (no tracking cookies)</li>
                <li><strong>No third-party analytics or tracking scripts</strong> — no PostHog, Google Analytics, Meta Pixel, Twitter widgets, TikTok/Instagram tracking, or session recorders run on this site.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">2.1 Identity Verification &amp; Encryption</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                Before any purchase, upload, or payout can be executed, every account must complete identity
                verification against a government-issued document. The document image and matching selfie
                are:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2 mt-3">
                <li>Encrypted in transit (TLS) and encrypted at rest with industry-standard cryptography.</li>
                <li>Stored in a dedicated verification store — never mixed with public content, profile data, or backups.</li>
                <li>Accessible only to a narrowly scoped review workflow. No employee has bulk-download access.</li>
                <li>Retained only as long as required to meet financial and regulatory obligations, then purged.</li>
              </ul>
              <p className="text-[#8A92A0] leading-relaxed mt-3">
                Verification records are used exclusively to confirm your identity for anti-fraud, sanctions,
                and payout compliance purposes. They are not used for marketing, are never sold, and are
                never provided to third parties except in response to a valid legal request.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">2.2 IP &amp; Content Protection Watermarking</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                To protect artists&apos; intellectual property, every audio and video file downloaded from the
                Platform carries a proprietary buyer-anchored protection signal. This signal is derived from
                the buyer&apos;s account identifier and the order metadata, and it enables the Platform to
                trace a leaked file back to the originating purchase. The signal does not contain your name,
                address, or payment details and is not shared with third parties. It exists solely to enable
                rights-holders and law enforcement to act on genuine leaks.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>Provide, maintain, and improve the Platform</li>
                <li>Process transactions and send related information</li>
                <li>Send promotional communications (with your consent)</li>
                <li>Respond to your comments and questions</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, investigate, and prevent fraudulent transactions</li>
                <li>Personalize and improve your experience</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">4. Information Sharing</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                We may share your information in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>With service providers who assist in our operations</li>
                <li>With artists when you purchase tickets or products</li>
                <li>In response to legal process or government requests</li>
                <li>To protect our rights, privacy, safety, or property</li>
                <li>In connection with a merger, acquisition, or sale of assets</li>
                <li>With your consent or at your direction</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">5. Data Security</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal data
                against unauthorized access, alteration, disclosure, or destruction. However, no method of
                transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee
                absolute security.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">5.1 Treasury &amp; Payments Safeguards (7 / 10 / 11 Rule)</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-3">
                StardomFans Ltd. operates under a documented three-window treasury model that governs how
                every payment matures on the Platform. This protects buyers, artists, and the Platform itself:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li><strong className="text-white">Days 1–7 · Refund window.</strong> Buyers may request a refund at any time. Funds are held in a Pending state and are not paid out.</li>
                <li><strong className="text-white">Days 8–10 · Treasury grace period.</strong> The refund window is closed, but the Platform continues to hold the funds to absorb chargebacks and disputes.</li>
                <li><strong className="text-white">Day 11+ · Matured.</strong> Funds are released into the artist&apos;s Available balance and the automated payout engine wires their share to their verified bank account.</li>
                <li><strong className="text-white">Sales tax</strong> collected at checkout is routed to a segregated treasury account and can only be remitted through a signed, dual-controlled release process.</li>
                <li>All treasury state transitions are recorded in an append-only ledger that is auditable and tamper-evident.</li>
              </ul>
              <p className="text-[#8A92A0] leading-relaxed mt-3 text-sm">
                Personal information used for identity verification and payout beneficiary matching is
                processed exclusively to satisfy these safeguards and applicable financial regulations.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">6. Your Rights</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal data:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>Access: Request a copy of your personal data</li>
                <li>Correction: Request correction of inaccurate data</li>
                <li>Deletion: Request deletion of your data</li>
                <li>Portability: Receive your data in a portable format</li>
                <li>Objection: Object to certain processing activities</li>
                <li>Withdrawal: Withdraw consent at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">7. Cookies</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                We use cookies and similar tracking technologies to track activity on our Platform and hold 
                certain information. Cookies are files with a small amount of data which may include an anonymous 
                unique identifier. You can instruct your browser to refuse all cookies or to indicate when a 
                cookie is being sent.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">8. Third-Party Services</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                Our Platform may contain links to third-party websites and services. We are not responsible 
                for the privacy practices of these third parties. We encourage you to read their privacy policies 
                before providing any information.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">9. Children's Privacy</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                StardomFans Ltd. is not intended for individuals under the age of 13. We do not knowingly collect 
                personal information from children under 13. If you are a parent or guardian and believe 
                your child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">10. Changes to This Policy</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last updated" date. You are 
                advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">10.1 Canadian Privacy Rights (PIPA / PIPEDA)</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                StardomFans Ltd. is operated under Canadian privacy law. Personal information collected from
                users located in British Columbia is protected by the{" "}
                <strong className="text-white">Personal Information Protection Act (BC)</strong>{" "}
                (&quot;PIPA&quot;). For users in the rest of Canada, the{" "}
                <strong className="text-white">Personal Information Protection and Electronic Documents Act</strong>{" "}
                (&quot;PIPEDA&quot;) applies. You have the right to access, correct, and request deletion of your
                personal information, and to withdraw consent to its use at any time by messaging admin in-app.
                Complaints may be escalated to the{" "}
                <strong className="text-white">Office of the Information &amp; Privacy Commissioner for British Columbia</strong>{" "}
                (OIPC BC).
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">11. Contact Us</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                If you have any questions about this Privacy Policy, our Privacy Officer can be reached at{" "}
                <a href="mailto:info@stardomfans.com" className="text-[#EC008C] hover:underline" data-testid="privacy-contact-email">
                  info@stardomfans.com
                </a>{" "}
                or through secure in-app messaging:{" "}
                <Link to="/message-admin" className="text-[#EC008C] hover:underline" data-testid="privacy-contact-admin">
                  Message Admin (Privacy)
                </Link>.
              </p>
              <p className="text-[#8A92A0] leading-relaxed mt-3 text-sm">
                Privacy Officer: StardomFans Ltd., Vancouver, British Columbia, Canada. Formal notices should
                be directed to <a href="mailto:info@stardomfans.com" className="text-[#EC008C] hover:underline">info@stardomfans.com</a>.
                Marketing correspondence and outbound broadcasts are not part of the Platform&apos;s design.
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <div className="flex gap-6 flex-wrap justify-center">
            <Link to="/terms" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Terms</Link>
            <Link to="/privacy" className="text-[#EC008C] text-sm">Privacy</Link>
            <Link to="/copyright" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Copyright</Link>
            <Link to="/security" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Security</Link>
            <Link to="/privacy-report" className="text-[#00E68A] text-sm font-semibold">Privacy Report</Link>
          </div>
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
